import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { clientHistory } from "../../utils/enum/enums";
import { sortByText } from "../../utils/sort/sort";
import { createCustomerLogService } from "../../services/logs/services";
import { useNotifications } from "../../context/Notifications";
import { getDepositTypesService } from "../../services/deposit/services";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

function ColdAccountDepositCommunicationModal({ customer, text, getClients }) {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const { addNotification } = useNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [type, setType] = useState(clientHistory.DEPOSIT);
  const [amount, setAmount] = useState("");
  const [depositTypes, setDepositTypes] = useState([]);
  const [depositType, setDepositType] = useState("");
  const [options, setOptions] = useState([
    {
      id: clientHistory.DEPOSIT,
      text: translate("deposit"),
    },
  ]);
  const [classCommunications] = useState({
    true: "flex w-2.5 h-2.5 bg-teal-500 rounded-full me-1.5 flex-shrink-0",
    false: "flex w-2.5 h-2.5 bg-red-500 rounded-full me-1.5 flex-shrink-0",
  });

  const toggleModal = () => {
    setIsLoading(false);
    setType(clientHistory.DEPOSIT);
    setComment("");
    setDepositType(depositTypes[0]?.code);
    setAmount("");
    setModalOpen(!modalOpen);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    const data = {
      accountNumber: customer?.accountNumber,
      type,
      agent: userProfile?.user.ext,
      comment,
      ...(type === clientHistory.DEPOSIT && {
        depositType,
        amount,
      }),
      companyId: company?._id,
    };

    const response = await createCustomerLogService(data);
    if (response?.logs?.acknowledged) {
      addNotification({
        id: Date.now(),
        message: translate("log-created"),
        kind: "success",
      });
      await getClients();
      toggleModal();
    } else {
      addNotification({
        id: Date.now(),
        message: translate("error-creating-log"),
        kind: "danger",
      });
    }
    setIsLoading(false);
  };

  const onChangeMessage = (event) => {
    const value = event.target.value;
    setComment(value);
  };

  const onChangeType = (event) => {
    const value = event.target.value;
    setType(value);
  };

  const onChangeDepositType = (event) => {
    const value = event.target.value;
    setDepositType(value);
  };

  const onChangeAmount = (event) => {
    const value = event.target.value;
    setAmount(value);
  };

  const getDepositTypesCall = React.useCallback(async () => {
    const data = await getDepositTypesService({
      companyId: company?._id,
    });
    setDepositTypes(data.deposits);
    setDepositType(depositTypes[0]?.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    setOptions((options) => {
      return sortByText(options);
    });

    getDepositTypesCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userLoading || companyLoading) return null;

  return (
    <>
      <span
        onClick={toggleModal}
        className="flex items-center text-sm font-medium me-3 text-blue-600 dark:text-blue-500 hover:underline"
      >
        <span
          className={classCommunications[customer.communications.deposit]}
        ></span>
        {text}: {customer.communications.deposit ? "Yes" : "No"}
      </span>

      {modalOpen && (
        <div
          id="modalID"
          tabIndex="-1"
          aria-hidden="true"
          className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto"
        >
          <div className="relative p-4 w-full max-w-2xl max-h-full">
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
              <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {translate("new-deposit")}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="modalID"
                  onClick={toggleModal}
                  disabled={isLoading}
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5">
                <div className="mb-6">
                  <label
                    htmlFor="type"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    {translate("select-type")}
                  </label>
                  <select
                    id="type"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    defaultValue={type}
                    required
                    onChange={onChangeType}
                    disabled
                  >
                    {options.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                </div>
                {type === clientHistory.DEPOSIT && (
                  <>
                    <div className="mb-6">
                      <label
                        htmlFor="typeDeposit"
                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {translate("deposit-type")}
                      </label>
                      <select
                        id="typeDeposit"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        defaultValue={depositType}
                        required
                        onChange={onChangeDepositType}
                      >
                        {depositTypes.map((deposit) => (
                          <option key={deposit._id} value={deposit.code}>
                            {deposit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-6">
                      <label
                        htmlFor="amount"
                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {translate("amount")}
                      </label>
                      <input
                        type="number"
                        id="amount"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Amount"
                        defaultValue={amount}
                        required
                        onChange={onChangeAmount}
                      />
                    </div>
                  </>
                )}
                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    required
                  >
                    {translate("comment")}
                  </label>
                  <textarea
                    id="message"
                    rows="4"
                    className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder={translate("comment-placeholder")}
                    defaultValue={comment}
                    required
                    onChange={onChangeMessage}
                  ></textarea>
                </div>

                <div className="flex justify-between items-center">
                  <button
                    data-modal-hide="modalID"
                    type="button"
                    className="py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                    onClick={toggleModal}
                    disabled={isLoading}
                  >
                    {translate("cancel")}
                  </button>
                  <button
                    type="button"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {translate("create-deposit")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ColdAccountDepositCommunicationModal;
